<template>
  <div v-if="isSending" class="modalDescription buyForm alignCenter contact-sending">
    <div class="loading-spinner" />
    <p>Sending your message...</p>
  </div>
  <div v-else-if="sendError" class="modalDescription buyForm alignCenter">
    <h1>Oops!</h1>
    <FeedbackMessage type="error" message="Something went wrong. Please try again." />
    <buttonCustom :button="'cancel'" :text="'Try again'" title="Try again" aria-label="Try again"
      @click="sendError = false">
      <template #icon><BackIcon /></template>
    </buttonCustom>
  </div>
  <div v-else-if="!isSent" class="modalDescription buyForm">
    <!-- On mobile the artwork title stays above the form, so it isn't repeated here -->
    <p class="form-subtitle">Inquire about</p>
    <h2 class="form-work-title">{{ work?.name }}</h2>
    <form @submit.prevent="send()">
      <div class="form-row">
        <label for="subject">Subject: </label>
        <div class="form-field">
          <select id="subject" v-model="client.subject" name="subject">
            <option value="purchase">Purchase interest</option>
            <option value="inquiry">General inquiry</option>
            <option value="commission">Commission request</option>
            <option value="other">Something else</option>
          </select>
        </div>
      </div>
      <div v-if="client.subject === 'other'" class="form-row">
        <label for="customSubject" />
        <div class="form-field">
          <input id="customSubject" v-model="client.customSubject" type="text" maxlength="200" name="customSubject" placeholder="please specify">
        </div>
      </div>
      <div class="form-row">
        <label for="clientName">Name: </label>
        <div class="form-field">
          <input id="clientName" v-model="client.name" type="text" maxlength="200" name="clientName" placeholder="your full name">
          <transition name="fade">
            <FeedbackMessage v-if="errors.name" type="error" :message="errors.name" />
          </transition>
        </div>
      </div>
      <div class="form-row">
        <label for="clientEmail">Email: </label>
        <div class="form-field">
          <input id="clientEmail" v-model="client.email" type="email" maxlength="254" name="clientEmail" placeholder="your@email.com">
          <transition name="fade">
            <FeedbackMessage v-if="errors.email" type="error" :message="errors.email" />
          </transition>
        </div>
      </div>
      <div class="form-row">
        <label for="clientPhone">Phone:<br><small>(optional)</small></label>
        <div class="form-field">
          <input id="clientPhone" v-model="client.phone" type="text" maxlength="50" name="clientPhone" placeholder="your phone number">
        </div>
      </div>
      <div class="form-row">
        <label for="clientMessage">Message: </label>
        <div class="form-field">
          <textarea id="clientMessage" v-model="client.message" name="clientMessage" maxlength="5000" placeholder="your message..." />
          <transition name="fade">
            <FeedbackMessage v-if="errors.message" type="error" :message="errors.message" />
          </transition>
        </div>
      </div>
      <div class="hp-field" aria-hidden="true">
        <label for="clientHp">Leave this empty</label>
        <input id="clientHp" v-model="website" type="text" name="hp_extra" tabindex="-1" autocomplete="off">
      </div>
      <div class="captcha-row">
        <div class="captcha-inline">
          <captcha @total-created="getAntiSpamTotal" />
          <input id="controlInputNumber" v-model.number="controlInputNumber" type="number" name="controlInputNumber" placeholder="Answer">
        </div>
        <transition name="fade">
          <FeedbackMessage v-if="errors.antispam" type="error" :message="errors.antispam" />
        </transition>
      </div>
      <div class="button-row">
        <buttonCustom type="submit" :button="'save'" :text="'Send Message'" title="Send Message"
          aria-label="Send Message">
          <template #icon><CheckIcon /></template>
        </buttonCustom>
        <buttonCustom type="reset" :button="'cancel'" :text="'Back'" title="Back" aria-label="Back"
          @click="$emit('goBack')">
          <template #icon><BackIcon /></template>
        </buttonCustom>
      </div>
    </form>
  </div>
  <transition v-else name="fade">
    <div class="modalDescription buyForm alignCenter">
      <h1>Thank you!</h1>
      <FeedbackMessage type="success" message="Your message has been sent!" />
      <buttonCustom :button="'cancel'" :text="'Back'" title="Back" aria-label="Back"
        @click="$emit('goBack')">
        <template #icon><BackIcon /></template>
      </buttonCustom>
    </div>
  </transition>
</template>


<script>
import Captcha from '@/components/Captcha.vue'
import FeedbackMessage from '@/components/FeedbackMessage.vue'
import BackIcon from '@/assets/icons/back.vue'
import CheckIcon from '@/assets/icons/check.vue'
import { submitInboxMessage } from '@/utils/submitInboxMessage'
import { getTenantUid } from '@/utils/tenantUtils'

export default {
  name: 'WorkContact',
  components: {
    'captcha': Captcha,
    FeedbackMessage,
    BackIcon,
    CheckIcon
  },
  props: {
    work: Object
  },
  emits: ['goBack'],
  data() {
    return {
      controlInputNumber: null,
      antiSpamNumber: null,
      errors: this.getFreshErrors(),
      client: this.getNewClient(),
      website: '',
      loadedAt: Date.now(),
      isSent: false,
      isSending: false,
      sendError: false
    }
  },
  methods: {
    getAntiSpamTotal(val) {
      this.antiSpamNumber = val
    },
    getFreshErrors() {
      return {
        email: null,
        antispam: null,
        name: null,
        message: null
      }
    },
    getNewClient() {
      return {
        name: null,
        email: null,
        phone: null,
        message: null,
        subject: 'purchase',
        customSubject: ''
      }
    },
    send() {
      this.errors = this.getFreshErrors()
      if (!this.client.name) {
        this.errors.name = 'Name required'
      }
      if (!this.client.email) {
        this.errors.email = 'Email required'
      }
      if (!this.client.message) {
        this.errors.message = 'Please enter a message'
      }
      if (this.controlInputNumber !== this.antiSpamNumber) {
        this.errors.antispam = 'Not the right answer. Please try again'
      }
      if (!this.controlInputNumber) {
        this.errors.antispam = 'Please write the sum of the numbers above'
      }
      if (this.client.email && !this.validEmail(this.client.email)) {
        this.errors.email = 'Valid email please'
      }

      const formHasNoErrors = !Object.values(this.errors).some(x => (x !== null && x !== ''))

      if (formHasNoErrors) {
        this.isSending = true
        let date = new Date()

        const subject = this.client.subject === 'other'
          ? (this.client.customSubject || 'Other')
          : this.client.subject

        const tenantUid = getTenantUid()
        const toSend = {
          uid: tenantUid,
          kind: 'inquiry',
          name: this.client.name,
          email: this.client.email,
          phone: this.client.phone || 'Not provided',
          message: this.client.message,
          subject,
          work: {
            id: this.work.id,
            slug: this.work.slug,
            name: this.work.name,
            imageThumb: this.work.imageThumb
          },
          formattedDate: date.toLocaleDateString('ro-RO') + ' ' + date.toLocaleTimeString('ro-RO'),
          website: this.website,
          elapsedMs: Date.now() - this.loadedAt
        }

        if (!tenantUid) {
          this.isSending = false
          this.sendError = true
          return
        }
        submitInboxMessage(toSend)
          .then(() => {
            this.isSending = false
            this.isSent = true
          })
          .catch(() => {
            this.isSending = false
            this.sendError = true
          })

      } else {
        return false
      }
    }
  }
}
</script>

<style scoped>

.modalDescription {
  flex: 1 1 40%;
  padding: 3em 1.2em 1.2em;
  font-size: 1em;
  overflow: hidden auto;

  @container work-modal (width >= 768px) {
    flex: 1 1 40%;
    max-height: 90vh;
  }

  @container work-modal (width < 768px) {
    flex: 1 0 100%;
    max-width: 100%;
    width: 100%;
    padding: 1.5em 2em;
    display: flex;
    flex-direction: column;
    align-items: center;

    h2 { width: 100%; }

    form {
      width: 100%;
    }
  }

  .form-subtitle {
    font-size: var(--font-xs);
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin: 0 0 0.6em;
    opacity: 0.7;
  }

  h2 {
    font-family: var(--font-decorative);
    font-weight: normal;
    font-size: 1.5em;
    margin-bottom: 2rem;
    text-align: center;
  }
}

@container work-modal (width < 768px) {
  .form-work-title {
    display: none;
  }
}

.form-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0;
  margin: 0 0 12px;
  gap: 8px;

  @container work-modal (width >= 1154px) {
    margin: 0 0 28px;
  }

  label {
    font-size: var(--font-sm);
    flex: 0 0 65px;
    text-align: left;
    line-height: 1.2;

    small {
      opacity: 0.7;
      font-size: 0.85em;
    }

    @container work-modal (width >= 1154px) {
      text-align: right;
    }
  }
}

.form-field {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;

  input,
  select,
  textarea {
    width: 100%;
    padding: 6px 8px;
    font-size: 14px;
    border: 1px solid var(--color-light-gray);
    border-radius: var(--radius);
    box-sizing: border-box;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: var(--color-muted);
    }
  }

  textarea {
    min-height: 60px;
    resize: vertical;
  }

  select {
    appearance: auto;
  }
}

.captcha-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0 0 12px;

  @container work-modal (width >= 1154px) {
    margin: 0 0 28px;
  }
}

.captcha-inline {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  & :deep(.captcha) {
    flex: 0 0 auto;
    white-space: nowrap;
    font-size: var(--font-sm);
  }

  input {
    width: 100px;
    padding: 6px 8px;
    font-size: 14px;
    border: 1px solid var(--color-light-gray);
    border-radius: var(--radius);
    box-sizing: border-box;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: var(--color-muted);
    }
  }
}

.button-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
}

.button-w-icon {
  margin: 0;
  width: 100%;
}

.alignCenter {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: inherit;
  gap: 3rem;
}

.contact-sending {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;

  p {
    margin-top: 16px;
    opacity: 0.7;
    font-size: 15px;
  }

  .loading-spinner {
    width: 48px;
    height: 48px;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
